import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { IsActiveMatchOptions, NavigationStart, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import type { Driver, DriveStep } from 'driver.js';
import { TourActionRegistryService } from './tour-action-registry.service';
import { TourCatalogService, TourKind, TourStepDefinition } from './tour-catalog.service';
import { TourContextService } from './tour-context.service';
import { UserTourStateService } from './user-tour-state.service';

export const CURRENT_DAY_ONE_TOUR_VERSION = 1;

type TourPhase = 'idle' | 'starting' | 'running' | 'finishing';
type TerminationReason =
  | 'done'
  | 'skip'
  | 'close'
  | 'route-change'
  | 'error'
  | 'manual-restart'
  | 'language-restart'
  | 'logout'
  | 'complete';
type StepPreparationResult =
  | { status: 'ready' }
  | {
      status: 'unavailable';
      reason:
        | 'route'
        | 'action-registration'
        | 'action-open'
        | 'selector-timeout'
        | 'target-stability-timeout';
    }
  | { status: 'fatal'; reason: string };
type TransitionMode = 'same-route' | 'cross-route';

const ROUTE_MATCH_OPTIONS: IsActiveMatchOptions = {
  paths: 'exact',
  queryParams: 'ignored',
  matrixParams: 'ignored',
  fragment: 'ignored'
};
const DEFAULT_SELECTOR_WAIT_TIMEOUT_MS = 4000;
const TARGET_STABILITY_TIMEOUT_MS = 1000;
const TARGET_STABILITY_TOLERANCE_PX = 1;

@Injectable({ providedIn: 'root' })
export class UserTourService {
  readonly phase = signal<TourPhase>('idle');

  private runId = 0;
  private activeRunId: number | null = null;
  private kind: TourKind | null = null;
  private driver: Driver | null = null;
  private activeSteps: TourStepDefinition[] = [];
  private activeIndex = -1;
  private hasPresentedStep = false;
  private terminationPromise: Promise<void> | null = null;
  private driverDestroyInProgress = false;
  private driverVisualRebuildInProgress = false;
  private readonly internallyDestroyedDriverVisuals = new WeakSet<Driver>();
  private tourNavigationInProgress = false;
  private stepTransitionInProgress = false;
  private pendingTransitionStep: {
    index: number;
    selector: string;
    mode: TransitionMode;
  } | null = null;
  private transitionRevealFrame: number | null = null;
  private readonly waitCleanups = new Map<number, Set<() => void>>();

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly router: Router,
    private readonly state: UserTourStateService,
    private readonly context: TourContextService,
    private readonly catalog: TourCatalogService,
    private readonly actions: TourActionRegistryService,
    private readonly translate: TranslateService,
    private readonly message: NzMessageService
  ) {
    this.router.events.subscribe((event) => {
      if (
        event instanceof NavigationStart &&
        this.activeRunId !== null &&
        !this.tourNavigationInProgress
      ) {
        void this.requestTermination(this.activeRunId, 'route-change');
      }
    });
    this.translate.onLangChange.subscribe(() => {
      const runId = this.activeRunId;
      const kind = this.kind;
      if (runId === null || !kind) {
        return;
      }
      void this.restartForLanguage(runId, kind);
    });
  }

  async startAutoTour(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !this.isDashboardRoute()) {
      return;
    }

    const runId = this.claimStart('day-one');
    if (runId === null) {
      return;
    }

    try {
      const state = await firstValueFrom(this.state.getDayOneState());
      if (!this.isCurrentRun(runId)) {
        return;
      }
      if (
        !state.dayOneTourEligible ||
        state.dayOneTourVersion >= CURRENT_DAY_ONE_TOUR_VERSION
      ) {
        return;
      }
      if (!this.isDashboardRoute()) {
        return;
      }
      await this.launch(runId, 'day-one');
    } catch {
      return;
    } finally {
      await this.releaseStartingRun(runId);
    }
  }

  async startManualTour(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.activeRunId !== null) {
      await this.requestTermination(this.activeRunId, 'manual-restart');
    }

    const runId = this.claimStart('manual');
    if (runId === null) {
      return;
    }

    try {
      await this.launch(runId, 'manual');
    } catch {
      if (this.isCurrentRun(runId)) {
        this.message.error(this.text('TOUR.ERROR.START'));
      }
    } finally {
      await this.releaseStartingRun(runId);
    }
  }

  async terminateActiveTour(): Promise<void> {
    if (this.activeRunId !== null) {
      await this.requestTermination(this.activeRunId, 'logout');
    }
  }

  private claimStart(kind: TourKind): number | null {
    if (this.phase() !== 'idle') {
      return null;
    }

    const runId = ++this.runId;
    this.activeRunId = runId;
    this.kind = kind;
    this.hasPresentedStep = false;
    this.phase.set('starting');
    return runId;
  }

  private async launch(runId: number, kind: TourKind): Promise<void> {
    const context = await this.context.load();
    if (!this.isCurrentRun(runId) || this.kind !== kind) {
      return;
    }

    const steps = this.catalog.build(kind, context);
    if (!steps.length) {
      return;
    }
    this.activeSteps = steps;

    const initialIndex = await this.findPreparedStep(runId, 0, 1);
    if (initialIndex === null || !this.isCurrentRun(runId)) {
      return;
    }

    this.activeIndex = initialIndex;
    const driver = await this.createDriverInstance(runId, kind);
    if (!driver) {
      return;
    }
    this.phase.set('running');
    try {
      driver.drive(initialIndex);
    } catch {
      await this.requestTermination(runId, 'error');
    }
  }

  private async createDriverInstance(runId: number, kind: TourKind): Promise<Driver | null> {
    const { driver } = await import('driver.js');
    if (!this.isCurrentRun(runId) || this.kind !== kind || !this.activeSteps.length) {
      return null;
    }

    const driverSteps: DriveStep[] = this.activeSteps.map((step) => ({
      element: step.selector,
      waitForElement: 0,
      skipMissingElement: false,
      popover: {
        title: this.text(step.titleKey),
        description: this.text(step.descriptionKey),
        side: step.side,
        align: 'center'
      }
    }));
    let instance: Driver | null = null;
    instance = driver({
      steps: driverSteps,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayClickBehavior: 'close',
      allowKeyboardControl: true,
      showProgress: kind === 'manual',
      progressText: this.text('TOUR.MANUAL.PROGRESS'),
      nextBtnText: this.text('TOUR.ACTIONS.NEXT'),
      prevBtnText: this.text('TOUR.ACTIONS.PREVIOUS'),
      doneBtnText: this.text('TOUR.ACTIONS.DONE'),
      closeBtnLabel: this.text('TOUR.ACTIONS.CLOSE'),
      popoverClass: 'scanafy-tour',
      waitForElement: 0,
      skipMissingElement: false,
      onNextClick: () => void this.move(runId, 1),
      onPrevClick: () => void this.move(runId, -1),
      onDoneClick: () => void this.completeFromExplicitAction(runId, 'done'),
      onCloseClick: () => void this.requestTermination(runId, 'close'),
      onDestroyStarted: () => {
        if (instance) {
          this.onDriverDestroyStarted(runId, instance);
        }
      },
      onDestroyed: () => {
        if (instance) {
          this.onDriverDestroyed(runId, instance);
        }
      },
      onHighlighted: (element, _step, options) => {
        if (instance) {
          this.onDriverHighlighted(runId, instance, element, options.index);
        }
      },
      onPopoverRender: (popover) => this.addDayOneSkip(runId, popover.footerButtons)
    });
    this.driver = instance;
    return instance;
  }

  private async completeFromExplicitAction(runId: number, action: 'done' | 'skip'): Promise<void> {
    if (!this.isCurrentRun(runId)) {
      return;
    }
    if (this.kind !== 'day-one') {
      await this.requestTermination(runId, 'complete');
      return;
    }

    await this.requestTermination(runId, action);
  }

  private addDayOneSkip(runId: number, footer: HTMLElement): void {
    if (this.kind !== 'day-one' || footer.querySelector('[data-tour-skip]')) {
      return;
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'driver-popover-skip-btn';
    button.dataset['tourSkip'] = 'true';
    button.textContent = this.text('TOUR.ACTIONS.SKIP');
    button.setAttribute('aria-label', this.text('TOUR.ACTIONS.SKIP'));
    button.addEventListener('click', () => void this.completeFromExplicitAction(runId, 'skip'));
    footer.prepend(button);
  }

  private async move(runId: number, direction: 1 | -1): Promise<void> {
    if (
      !this.isCurrentRun(runId) ||
      this.phase() !== 'running' ||
      this.stepTransitionInProgress
    ) {
      return;
    }

    const sourceStep = this.activeSteps[this.activeIndex];
    this.beginStepTransition();
    let awaitingDriverReveal = false;
    let requiresDriverVisualRebuild = false;
    try {
      await this.closeActiveStepAction(runId);
      const index = await this.findPreparedStep(
        runId,
        this.activeIndex + direction,
        direction,
        async (candidate) => {
          if (
            requiresDriverVisualRebuild ||
            this.transitionModeFor(sourceStep, candidate) !== 'cross-route'
          ) {
            return true;
          }
          const rebuilt = await this.destroyDriverVisualForRouteChange(runId);
          requiresDriverVisualRebuild = rebuilt;
          return rebuilt;
        }
      );
      if (!this.isCurrentRun(runId)) {
        return;
      }
      if (index === null) {
        if (direction === 1) {
          await this.requestTermination(runId, 'complete');
        } else {
          awaitingDriverReveal = await this.restoreCurrentStep(runId);
          if (!awaitingDriverReveal) {
            await this.requestTermination(runId, 'error');
          }
        }
        return;
      }
      this.activeIndex = index;
      const destinationStep = this.activeSteps[index];
      const transitionMode = this.transitionModeFor(sourceStep, destinationStep);
      if (requiresDriverVisualRebuild) {
        const kind = this.kind;
        if (!kind || !await this.createDriverInstance(runId, kind)) {
          await this.requestTermination(runId, 'error');
          return;
        }
      }
      awaitingDriverReveal = this.drivePendingTransitionStep(
        runId,
        index,
        transitionMode
      );
      if (!awaitingDriverReveal) {
        await this.requestTermination(runId, 'error');
      }
    } finally {
      if (!awaitingDriverReveal || !this.isCurrentRun(runId)) {
        this.finishStepTransition();
      }
    }
  }

  private async findPreparedStep(
    runId: number,
    startIndex: number,
    direction: 1 | -1,
    beforePrepare?: (step: TourStepDefinition, index: number) => Promise<boolean>
  ): Promise<number | null> {
    for (
      let index = startIndex;
      index >= 0 && index < this.activeSteps.length;
      index += direction
    ) {
      const step = this.activeSteps[index];
      if (beforePrepare && !await beforePrepare(step, index)) {
        if (this.isCurrentRun(runId)) {
          await this.requestTermination(runId, 'error');
        }
        return null;
      }
      const result = await this.prepareStep(runId, step);
      if (!this.isCurrentRun(runId)) {
        return null;
      }
      if (result.status === 'ready') {
        return index;
      }
      if (result.status === 'fatal' || step.onUnavailable === 'abort') {
        await this.requestTermination(runId, 'error');
        return null;
      }
    }
    return null;
  }

  private async prepareStep(runId: number, step: TourStepDefinition): Promise<StepPreparationResult> {
    const navigated = await this.navigateToStep(runId, step.route);
    if (!navigated) {
      return { status: 'unavailable', reason: 'route' };
    }
    if (!this.isCurrentRun(runId)) {
      return { status: 'fatal', reason: 'stale-run' };
    }

    if (step.openAction) {
      try {
        const registered = await this.waitForActionRegistration(runId, step.openAction);
        if (!registered) {
          return { status: 'unavailable', reason: 'action-registration' };
        }
        if (!this.isCurrentRun(runId)) {
          return { status: 'fatal', reason: 'stale-run' };
        }
        const handle = await this.actions.open(runId, step.openAction);
        if (!handle) {
          return { status: 'unavailable', reason: 'action-open' };
        }
      } catch {
        return { status: 'unavailable', reason: 'action-open' };
      }
    }

    const foundTarget = await this.waitForSelector(runId, step.selector, step.selectorTimeoutMs);
    const stabilitySelector = step.stabilitySelector ?? step.selector;
    const foundStabilityTarget = stabilitySelector === step.selector || await this.waitForSelector(
      runId,
      stabilitySelector,
      step.selectorTimeoutMs
    );
    if (!foundTarget || !foundStabilityTarget) {
      if (step.openAction) {
        await this.actions.close(runId, step.closeAction || step.openAction).catch(() => undefined);
      }
      return { status: 'unavailable', reason: 'selector-timeout' };
    }
    if (await this.waitForStableTarget(runId, stabilitySelector)) {
      return { status: 'ready' };
    }
    if (step.openAction) {
      await this.actions.close(runId, step.closeAction || step.openAction).catch(() => undefined);
    }
    return { status: 'unavailable', reason: 'target-stability-timeout' };
  }

  private async navigateToStep(runId: number, route: string): Promise<boolean> {
    const routeTree = this.router.createUrlTree([route]);
    if (this.router.isActive(routeTree, ROUTE_MATCH_OPTIONS)) {
      return true;
    }
    this.tourNavigationInProgress = true;
    try {
      const success = await this.router.navigateByUrl(routeTree);
      return success && this.isCurrentRun(runId) && this.router.isActive(routeTree, ROUTE_MATCH_OPTIONS);
    } catch {
      return false;
    } finally {
      this.tourNavigationInProgress = false;
    }
  }

  private waitForSelector(
    runId: number,
    selector: string,
    timeoutMs = DEFAULT_SELECTOR_WAIT_TIMEOUT_MS
  ): Promise<boolean> {
    if (document.querySelector(selector)) {
      return Promise.resolve(true);
    }
    if (timeoutMs <= 0) {
      return Promise.resolve(false);
    }
    return new Promise<boolean>((resolve) => {
      let settled = false;
      let timeout: number | null = null;
      const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
          settle(true);
        }
      });
      const settle = (value: boolean) => {
        if (settled) {
          return;
        }
        settled = true;
        observer.disconnect();
        if (timeout !== null) {
          window.clearTimeout(timeout);
        }
        this.waitCleanups.get(runId)?.delete(cancel);
        resolve(value);
      };
      const cancel = () => settle(false);
      timeout = window.setTimeout(cancel, timeoutMs);
      const cleanups = this.waitCleanups.get(runId) || new Set<() => void>();
      cleanups.add(cancel);
      this.waitCleanups.set(runId, cleanups);
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  private async waitForActionRegistration(runId: number, actionId: string): Promise<boolean> {
    const controller = new AbortController();
    const cancel = () => controller.abort();
    const cleanups = this.waitCleanups.get(runId) || new Set<() => void>();
    cleanups.add(cancel);
    this.waitCleanups.set(runId, cleanups);
    try {
      return await this.actions.waitUntilRegistered(actionId, { signal: controller.signal });
    } finally {
      this.waitCleanups.get(runId)?.delete(cancel);
    }
  }

  private waitForStableTarget(runId: number, selector: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      let settled = false;
      let frameId: number | null = null;
      let timeout: number | null = null;
      let previousTarget: HTMLElement | null = null;
      let previousBounds: DOMRect | null = null;
      let stableFrames = 0;
      let cancel: () => void;

      const settle = (value: boolean) => {
        if (settled) {
          return;
        }
        settled = true;
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
        }
        if (timeout !== null) {
          window.clearTimeout(timeout);
        }
        this.waitCleanups.get(runId)?.delete(cancel);
        resolve(value);
      };
      cancel = () => settle(false);
      const isSameBounds = (left: DOMRect, right: DOMRect) =>
        Math.abs(left.top - right.top) <= TARGET_STABILITY_TOLERANCE_PX &&
        Math.abs(left.right - right.right) <= TARGET_STABILITY_TOLERANCE_PX &&
        Math.abs(left.bottom - right.bottom) <= TARGET_STABILITY_TOLERANCE_PX &&
        Math.abs(left.left - right.left) <= TARGET_STABILITY_TOLERANCE_PX;
      const verify = () => {
        if (!this.isCurrentRun(runId)) {
          settle(false);
          return;
        }

        const target = document.querySelector<HTMLElement>(selector);
        if (!target || !target.isConnected) {
          previousTarget = null;
          previousBounds = null;
          stableFrames = 0;
          frameId = requestAnimationFrame(verify);
          return;
        }

        const bounds = target.getBoundingClientRect();
        if (bounds.width <= 0 || bounds.height <= 0) {
          previousTarget = null;
          previousBounds = null;
          stableFrames = 0;
          frameId = requestAnimationFrame(verify);
          return;
        }

        stableFrames = previousTarget === target && previousBounds && isSameBounds(previousBounds, bounds)
          ? stableFrames + 1
          : 0;
        previousTarget = target;
        previousBounds = bounds;
        if (stableFrames >= 2) {
          settle(true);
          return;
        }
        frameId = requestAnimationFrame(verify);
      };

      const cleanups = this.waitCleanups.get(runId) || new Set<() => void>();
      cleanups.add(cancel);
      this.waitCleanups.set(runId, cleanups);
      timeout = window.setTimeout(() => settle(false), TARGET_STABILITY_TIMEOUT_MS);
      frameId = requestAnimationFrame(verify);
    });
  }

  private async closeActiveStepAction(runId: number): Promise<void> {
    const step = this.activeSteps[this.activeIndex];
    if (step?.openAction) {
      await this.actions.close(runId, step.closeAction || step.openAction).catch(() => undefined);
    }
  }

  private async restoreCurrentStep(runId: number): Promise<boolean> {
    const step = this.activeSteps[this.activeIndex];
    if (!step) {
      return false;
    }
    const result = await this.prepareStep(runId, step);
    if (!this.isCurrentRun(runId) || result.status !== 'ready') {
      return false;
    }
    return this.drivePendingTransitionStep(runId, this.activeIndex, 'same-route');
  }

  private drivePendingTransitionStep(
    runId: number,
    index: number,
    mode: TransitionMode
  ): boolean {
    const step = this.activeSteps[index];
    const target = step && document.querySelector(step.selector);
    const driver = this.driver;
    if (!step || !target?.isConnected || !driver || !this.isCurrentRun(runId)) {
      return false;
    }
    this.pendingTransitionStep = { index, selector: step.selector, mode };
    try {
      driver.drive(index);
      return true;
    } catch {
      this.clearPendingTransitionStep();
      return false;
    }
  }

  private destroyDriverVisualForRouteChange(runId: number): Promise<boolean> {
    if (!this.isCurrentRun(runId) || this.terminationPromise) {
      return Promise.resolve(false);
    }

    const driver = this.driver;
    if (!driver) {
      return Promise.resolve(true);
    }

    this.driverVisualRebuildInProgress = true;
    this.internallyDestroyedDriverVisuals.add(driver);
    try {
      driver.destroy();
      if (this.driver === driver) {
        this.driver = null;
      }
      return Promise.resolve(true);
    } catch {
      return Promise.resolve(false);
    } finally {
      this.driverVisualRebuildInProgress = false;
    }
  }

  private transitionModeFor(
    sourceStep: TourStepDefinition | undefined,
    destinationStep: TourStepDefinition | undefined
  ): TransitionMode {
    if (!sourceStep || !destinationStep) {
      return 'same-route';
    }
    const sourceRoute = this.router.serializeUrl(this.router.createUrlTree([sourceStep.route]));
    const destinationRoute = this.router.serializeUrl(this.router.createUrlTree([destinationStep.route]));
    return sourceRoute === destinationRoute ? 'same-route' : 'cross-route';
  }

  private beginStepTransition(): void {
    this.stepTransitionInProgress = true;
    document.body.classList.add('scanafy-tour-transitioning');
    this.clearPendingTransitionStep();
  }

  private finishStepTransition(): void {
    this.clearPendingTransitionStep();
    this.stepTransitionInProgress = false;
    document.body.classList.remove('scanafy-tour-transitioning');
  }

  private onDriverHighlighted(
    runId: number,
    callbackDriver: Driver,
    element: Element | undefined,
    index: number | undefined
  ): void {
    const pending = this.pendingTransitionStep;
    const driver = this.driver;
    if (
      driver === callbackDriver &&
      this.isCurrentRun(runId) &&
      element?.isConnected &&
      index !== undefined &&
      driver.getActiveIndex() === index &&
      driver.getActiveElement() === element
    ) {
      this.hasPresentedStep = true;
    }
    if (
      !pending ||
      !driver ||
      driver !== callbackDriver ||
      !this.isCurrentRun(runId) ||
      !this.stepTransitionInProgress ||
      index !== pending.index ||
      driver.getActiveIndex() !== pending.index
    ) {
      return;
    }

    const target = document.querySelector(pending.selector);
    if (!target || !target.isConnected || element !== target || driver.getActiveElement() !== target) {
      return;
    }
    if (this.transitionRevealFrame !== null) {
      return;
    }

    driver.refresh();
    this.transitionRevealFrame = requestAnimationFrame(() => {
      this.transitionRevealFrame = null;
      if (!this.isPendingTransitionTarget(runId, pending, target)) {
        return;
      }
      this.driver?.refresh();
      this.finishStepTransition();
    });
  }

  private isPendingTransitionTarget(
    runId: number,
    pending: { index: number; selector: string; mode: TransitionMode },
    target: Element
  ): boolean {
    const driver = this.driver;
    return (
      this.isCurrentRun(runId) &&
      this.stepTransitionInProgress &&
      this.pendingTransitionStep === pending &&
      document.querySelector(pending.selector) === target &&
      target.isConnected &&
      driver?.getActiveIndex() === pending.index &&
      driver?.getActiveElement() === target
    );
  }

  private clearPendingTransitionStep(): void {
    this.pendingTransitionStep = null;
    if (this.transitionRevealFrame !== null) {
      cancelAnimationFrame(this.transitionRevealFrame);
      this.transitionRevealFrame = null;
    }
  }

  private onDriverDestroyStarted(runId: number, driver: Driver): void {
    if (
      !this.isCurrentRun(runId) ||
      this.driverDestroyInProgress ||
      this.driverVisualRebuildInProgress ||
      this.internallyDestroyedDriverVisuals.has(driver)
    ) {
      return;
    }
    void this.requestTermination(runId, 'close');
  }

  private onDriverDestroyed(runId: number, driver: Driver): void {
    if (
      !this.isCurrentRun(runId) ||
      this.terminationPromise ||
      this.driverVisualRebuildInProgress ||
      this.internallyDestroyedDriverVisuals.has(driver)
    ) {
      return;
    }
    void this.requestTermination(runId, 'close');
  }

  private requestTermination(runId: number, reason: TerminationReason): Promise<void> {
    if (!this.isCurrentRun(runId)) {
      return Promise.resolve();
    }
    if (this.terminationPromise) {
      return this.terminationPromise;
    }
    this.phase.set('finishing');
    const driver = this.driver;
    const acknowledgementPromise = this.acknowledgeDayOneIfNeeded(reason);
    this.terminationPromise = (async () => {
      try {
        this.finishStepTransition();
        this.clearWaits(runId);
        try {
          await this.actions.closeAll(runId);
        } catch {
          this.message.error(this.text('TOUR.ERROR.START'));
        }
        if (driver) {
          this.driverDestroyInProgress = true;
          try {
            driver.destroy();
          } finally {
            this.driverDestroyInProgress = false;
          }
        }
      } finally {
        await acknowledgementPromise;
        this.finalizeDestroyed(runId);
      }
    })();
    return this.terminationPromise;
  }

  private async acknowledgeDayOneIfNeeded(reason: TerminationReason): Promise<void> {
    if (
      this.kind !== 'day-one' ||
      !this.hasPresentedStep ||
      !['done', 'skip', 'close', 'complete', 'route-change'].includes(reason)
    ) {
      return;
    }

    try {
      await firstValueFrom(this.state.completeDayOne(CURRENT_DAY_ONE_TOUR_VERSION));
    } catch {
      this.message.error(this.text('TOUR.ERROR.SAVE'));
    }
  }

  private async releaseStartingRun(runId: number): Promise<void> {
    if (this.isCurrentRun(runId) && this.phase() === 'starting') {
      this.finishStepTransition();
      this.clearWaits(runId);
      try {
        await this.actions.closeAll(runId);
      } catch {
        this.message.error(this.text('TOUR.ERROR.START'));
      } finally {
        this.finalizeDestroyed(runId);
      }
    }
  }

  private async restartForLanguage(runId: number, kind: TourKind): Promise<void> {
    await this.requestTermination(runId, 'language-restart');
    if (kind === 'manual') {
      await this.startManualTour();
      return;
    }
    await this.startAutoTour();
  }

  private finalizeDestroyed(runId: number): void {
    if (!this.isCurrentRun(runId)) {
      return;
    }
    this.clearWaits(runId);
    this.finishStepTransition();
    this.driver = null;
    this.kind = null;
    this.activeRunId = null;
    this.activeSteps = [];
    this.activeIndex = -1;
    this.hasPresentedStep = false;
    this.terminationPromise = null;
    this.driverDestroyInProgress = false;
    this.driverVisualRebuildInProgress = false;
    this.phase.set('idle');
  }

  private clearWaits(runId: number): void {
    const cleanups = this.waitCleanups.get(runId);
    if (!cleanups) {
      return;
    }
    this.waitCleanups.delete(runId);
    cleanups.forEach((cleanup) => cleanup());
  }

  private isCurrentRun(runId: number): boolean {
    return this.activeRunId === runId;
  }

  private isDashboardRoute(): boolean {
    return this.router.isActive(this.router.createUrlTree(['/dashboard']), ROUTE_MATCH_OPTIONS);
  }

  private text(key: string): string {
    return this.translate.instant(key);
  }
}
